import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Award, ClipboardList, FileText } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { todayLocal } from "@/lib/toolbox";
import { useMyToolboxAssignments } from "@/hooks/use-my-assignment-count";
import { MyAssignmentsView } from "@/routes/_authenticated/documents";
import { AssignedCertsBanner, type MyCertAssignment } from "@/routes/_authenticated/my-certifications";

type AssignmentsTab = "docs" | "certs" | "toolbox";

export const Route = createFileRoute("/_authenticated/my-assignments")({
  validateSearch: (s: Record<string, unknown>): { tab?: AssignmentsTab } =>
    s.tab === "docs" || s.tab === "certs" || s.tab === "toolbox" ? { tab: s.tab } : {},
  component: MyAssignmentsPage,
});

/** Everything assigned to the signed-in worker, one tab per kind of assignment. */
function MyAssignmentsPage() {
  const { tab = "docs" } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: certData } = useQuery({
    queryKey: ["my-cert-assignments"],
    queryFn: () => apiGet<{ data: MyCertAssignment[] }>("/api/certifications/my-assignments"),
    staleTime: 30_000,
  });
  const { data: docData } = useQuery({
    queryKey: ["my-doc-assignments"],
    queryFn: () => apiGet<{ data: { completionStatus: string }[] }>("/api/doc-assignments/mine"),
    staleTime: 30_000,
  });
  const { data: toolboxData } = useMyToolboxAssignments();
  const toolboxes = toolboxData?.data ?? [];
  const certs = (certData?.data ?? []).filter((a) => a.status !== "approved");
  const docCount = (docData?.data ?? []).filter((a) => a.completionStatus !== "approved").length;

  const tabs: { id: AssignmentsTab; label: string; icon: typeof FileText; count: number }[] = [
    { id: "docs", label: "Documents", icon: FileText, count: docCount },
    { id: "certs", label: "Credentials", icon: Award, count: certs.length },
    { id: "toolbox", label: "Toolbox", icon: ClipboardList, count: toolboxes.length },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-4 pb-24">
      <h1 className="text-2xl font-bold text-text-primary pt-2">My Assignments</h1>

      <div className="flex rounded-lg border border-border bg-muted/40 p-1 gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => navigate({ to: "/my-assignments", search: { tab: t.id }, replace: true })}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              tab === t.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {t.count > 0 && (
              <span className="ml-0.5 min-w-[18px] rounded-full bg-paris-red px-1.5 text-[10px] font-bold leading-[18px] text-white">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "docs" ? (
        <MyAssignmentsView />
      ) : tab === "toolbox" ? (
        toolboxes.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No toolbox meetings assigned to you right now.</p>
        ) : (
          <div className="space-y-2">
            {toolboxes.map((a) => {
              const overdue = !!a.dueDate && a.dueDate < todayLocal();
              const body = (
                <>
                  <span className="block text-sm font-semibold">{a.title}</span>
                  <span className="block text-xs text-muted-foreground">{[a.projectName, a.assignedByName ? `from ${a.assignedByName}` : null].filter(Boolean).join(" · ")}</span>
                  <span className={cn("block text-xs", overdue ? "font-semibold text-red-600" : "text-muted-foreground")}>{a.dueDate ? `Due ${formatDateOnly(a.dueDate)}${overdue ? " (overdue)" : ""}` : "No due date"}</span>
                  {a.shared && !a.meetingId && <span className="mt-0.5 block text-xs text-amber-700 dark:text-amber-400">Any foreman on the site can take it: the first to start it takes it.</span>}
                  {a.note && <span className="mt-0.5 block whitespace-pre-line text-xs">{a.note}</span>}
                  <span className="mt-2 inline-block rounded-md bg-paris-red px-3 py-1.5 text-xs font-semibold text-white">{a.meetingId ? "Continue" : "Start the toolbox"}</span>
                </>
              );
              const cls = "block rounded-xl border bg-card px-4 py-3 hover:bg-muted/30 transition-colors";
              return a.meetingId ? (
                <Link key={a.targetId} to="/toolbox/$meetingId" params={{ meetingId: a.meetingId }} className={cls}>{body}</Link>
              ) : (
                <Link key={a.targetId} to="/toolbox/new" search={{ assignment: a.targetId }} className={cls}>{body}</Link>
              );
            })}
          </div>
        )
      ) : certs.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No credentials assigned to you right now.</p>
      ) : (
        <AssignedCertsBanner
          assignments={certs}
          onUpload={(certTypeId) => navigate({ to: "/my-certifications", search: { upload: certTypeId } })}
          onClaimed={() => qc.invalidateQueries({ queryKey: ["my-cert-assignments"] })}
        />
      )}
    </div>
  );
}
